import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import { getAuth, onAuthStateChanged, signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, getDoc, getDocFromServer, setDoc, deleteDoc, updateDoc, collection, query, where, limit, getDocs, enableNetwork, onSnapshot } from 'firebase/firestore';
import { db, storage } from '../config/firebase';
import Constants from 'expo-constants';
import { authService } from '../services/authService';


const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const lastUidRef = useRef(null);
  const [devModeUser, setDevModeUser] = useState(() => {
    if (process.env.NODE_ENV === 'development') {
      return localStorage.getItem('devModeUser') || null;
    }
    return null;
  });
  
  const isDev = process.env.NODE_ENV === 'development';


  const register = async (email, password, firstName, lastName, mode, autoUpdate) => {
    const { user, error } = await authService.register(email, password, firstName, lastName, mode, autoUpdate);
    await new Promise(resolve => setTimeout(resolve, 1000));
    setUser(user);
    return {user, error};
  };

  const signIn = async (email, password) => {
    const { user, error } = await authService.login(email, password);
    if (user) {
      setUser(user);
    }
    return {user, error};
  };

  const signInWithGoogleAuth = async () => {
    const { user, error } = await authService.signInWithGoogle();
    if (user) {
      setUser(user);
    }
    return { user, error };
  };

  const logout = async () => {
    const { error } = await authService.logout();
    setUser(null);
    lastUidRef.current = null;
    return { error };
  };

  const resetPassword = async (email) => {
    try {
      const auth = getAuth();
      await sendPasswordResetEmail(auth, email);
      return { success: true };
    } catch (error) {
      console.error('Error sending password reset email:', error);
      return { success: false, error: error.message };
    }
  };

  // Add network initialization
  useEffect(() => {
    const initializeFirestore = async () => {
      try {
        await enableNetwork(db);
      } catch (error) {
        console.error('Error enabling Firestore network:', error);
      }
    };

    initializeFirestore();
  }, []);

  const authListenerRef = useRef(null);
  useEffect(() => {
    // Prevent duplicate auth listeners
    if (authListenerRef.current) {
      return;
    }

    const auth = getAuth();
    
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      
      try {
        if (firebaseUser) {
          const userRef = doc(db, 'users', firebaseUser.uid);
          
          // Try to fetch fresh from server, fallback to cache if it fails
          let userDoc;
          try {
            userDoc = await getDocFromServer(userRef);
          } catch (serverError) {
            userDoc = await getDoc(userRef);
          }

          // Default preferences
          const defaultPreferences = {
            darkMode: true,
            emailNotifications: true,
            autoUpdate: true,
          };

          let mergedUser;

          if (userDoc.exists()) {
            // User document exists - use it
            const userData = userDoc.data();
            
            // DEBUG: Log what we're getting from Firestore
            console.log('[AuthContext] Firestore userData:', {
              userType: userData.userType,
              allData: userData
            });

            mergedUser = {
              ...userData,
              uid: firebaseUser.uid,
              email: firebaseUser.email, // Always use Firebase Auth email as source of truth
              preferences: userData.preferences || defaultPreferences,
              userType: userData.userType || 'user', // From Firestore, default to 'user'
            };
            
            console.log('[AuthContext] Merged user:', {
              userType: mergedUser.userType,
              uid: mergedUser.uid
            });
          } else {
            // No user document - create a basic one
            mergedUser = {
              uid: firebaseUser.uid,
              email: firebaseUser.email || '',
              firstName: '',
              lastName: '',
              username: '',
              phoneNumber: '',
              profilePhoto: '',
              preferences: defaultPreferences,
              userType: 'user', // Default for new users
              createdAt: new Date(),
            };

            // Create the document (non-blocking)
            setDoc(userRef, mergedUser, { merge: true }).catch(err => 
              console.error('Could not create user document:', err)
            );
          }

          setUser(mergedUser);
          lastUidRef.current = firebaseUser.uid;
          
          // Navigation logic is handled by NavigationContext
          // AuthContext just manages auth state
        } else {
          setUser(null);
          lastUidRef.current = null;
        }
      } catch (error) {
        // Only log if it's not a duplicate target error
        if (!error.message?.includes('Target ID already exists')) {
          console.error('Error in auth state change:', error);
        }
        if (!isDev) {
          setUser(null);
        }
      } finally {
        setLoading(false);
      }
    });

    authListenerRef.current = unsubscribe;

    return () => {
      if (authListenerRef.current) {
        authListenerRef.current();
        authListenerRef.current = null;
      }
    };
  }, []);

  // Realtime user listener - updates user state when user properties change in Firestore
  // This allows admin role changes, intake resets, etc. to apply immediately without re-login
  const userTypeListenerRef = useRef(null);
  useEffect(() => {
    if (!user?.uid) {
      // Cleanup if user logs out
      if (userTypeListenerRef.current) {
        userTypeListenerRef.current();
        userTypeListenerRef.current = null;
      }
      return;
    }
    
    // Prevent duplicate listeners
    if (userTypeListenerRef.current) {
      return;
    }
    
    const userRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(
      userRef,
      (snap) => {
        if (snap.exists()) {
          // IMPORTANT: Skip if data is from cache to avoid overwriting fresh server data
          // This prevents stale cached 'user' from overwriting fresh 'admin' from server
          if (snap.metadata.fromCache) {
            console.log('[AuthContext] Skipping cached realtime update (fromCache=true)');
            return;
          }
          
          const data = snap.data();
          const fetchedUserType = data.userType || 'user';
          const fetchedHasCompletedIntake = data.hasCompletedIntake || false;
          const fetchedCommitmentLevel = data.commitmentLevel || null;
          
          console.log('[AuthContext] Realtime user update (from server):', {
            fromFirestore: {
              userType: fetchedUserType,
              hasCompletedIntake: fetchedHasCompletedIntake,
              commitmentLevel: fetchedCommitmentLevel
            },
            currentInState: {
              userType: user.userType,
              hasCompletedIntake: user.hasCompletedIntake,
              commitmentLevel: user.commitmentLevel
            }
          });
          
          // Update user state with any changed properties
          const hasChanges = 
            user.userType !== fetchedUserType ||
            user.hasCompletedIntake !== fetchedHasCompletedIntake ||
            user.commitmentLevel !== fetchedCommitmentLevel;
            
          if (hasChanges) {
            console.log('[AuthContext] Updating user properties');
            setUser(prev => prev ? { 
              ...prev, 
              userType: fetchedUserType,
              hasCompletedIntake: fetchedHasCompletedIntake,
              commitmentLevel: fetchedCommitmentLevel
            } : prev);
          }
        }
      },
      (error) => {
        // Only log if it's not a duplicate target error
        if (!error.message?.includes('Target ID already exists')) {
          console.error('[AuthContext] Error fetching userType:', error);
        }
      }
    );
    
    userTypeListenerRef.current = unsubscribe;
    
    return () => {
      if (userTypeListenerRef.current) {
        userTypeListenerRef.current();
        userTypeListenerRef.current = null;
      }
    };
  }, [user?.uid]);

  // Dev mode auto-login
  useEffect(() => {
    const handleDevModeAutoLogin = async () => {
      if (isDev && devModeUser) {
        try {
          const auth = getAuth();
          const password = process.env.REACT_APP_DEV_PASSWORD;
          if (!password) {
            throw new Error('Development password not found');
          }

          if (!auth.currentUser || auth.currentUser.email !== devModeUser) {
            await signInWithEmailAndPassword(auth, devModeUser, password);
          }
        } catch (error) {
          console.error('Dev mode auto-login error:', error);
          localStorage.removeItem('devModeUser');
          setDevModeUser(null);
        }
      }
    };

    handleDevModeAutoLogin();
  }, [devModeUser, isDev]);

  // Store devModeUser in localStorage
  useEffect(() => {
    if (isDev) {
      if (devModeUser) {
        localStorage.setItem('devModeUser', devModeUser);
      } else {
        localStorage.removeItem('devModeUser');
      }
    }
  }, [devModeUser, isDev]);

  // Load user profile when user changes
  useEffect(() => {
    if (user?.uid) {
      loadUserProfile();
    } else {
      setUserProfile(null);
    }
  }, [user?.uid]);

  // Load user profile from Firestore
  const loadUserProfile = async () => {
    if (!user?.uid) return;
    
    setProfileLoading(true);
    
    try {
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        // Merge with default preferences if some are missing
        const profileData = userSnap.data();
        setUserProfile(profileData);
      } else {
        // Default preferences
        const defaultPreferences = {
          darkMode: true,
          emailNotifications: true,
          autoUpdate: true,
        };

        // If no user profile exists, create with defaults
        const newUserProfile = {
          uid: user.uid,
          username: user.displayName || '',
          email: user.email || '',
          phoneNumber: user.phoneNumber || '',
          profilePhoto: user.photoURL || '',
          preferences: defaultPreferences,
        };
        setUserProfile(newUserProfile);
        // Save the new profile to Firestore
        await setDoc(userRef, newUserProfile, { merge: true });
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    } finally {
      setProfileLoading(false);
    }
  };

  // Save user profile to Firestore
  const saveUserProfile = async (updatedProfile) => {
    if (!user?.uid) {
      return { success: false, error: 'No user ID found' };
    }

    setProfileLoading(true);

    try {
      const userRef = doc(db, 'users', user.uid);
      await setDoc(userRef, updatedProfile, { merge: true });
      
      // Update both userProfile and user state to keep UI in sync
      setUserProfile(prev => ({ ...prev, ...updatedProfile }));
      setUser(prev => prev ? { ...prev, ...updatedProfile } : prev);
      
      return { success: true };
    } catch (error) {
      console.error('Error saving user profile:', error);
      return { success: false, error: error.message };
    } finally {
      setProfileLoading(false);
    }
  };

  const uploadProfilePhoto = async (fileBlob, fileName) => {
    if (!user?.uid) {
      return { success: false, error: 'No user ID found' };
    }

    setProfileLoading(true);

    try {
      // Path must match storage rules: userProfilePhotos/{userId}/{fileName}
      const storageRef = ref(storage, `userProfilePhotos/${user.uid}/${fileName}`);
      await uploadBytes(storageRef, fileBlob);
      const photoURL = await getDownloadURL(storageRef);
      
      // Update user profile with photo URL
      const updatedProfile = { profilePhoto: photoURL, photoURL: photoURL };
      await saveUserProfile(updatedProfile);
      
      return { success: true, photoURL };
    } catch (error) {
      console.error('Error uploading profile photo:', error);
      return { success: false, error: error.message };
    } finally {
      setProfileLoading(false);
    }
  };


  // Remove profile photo (rebuilt)
  const removeProfilePhoto = async () => {
    if (!user?.uid) {
      console.error('[removeProfilePhoto] No user ID');
      return { success: false, error: 'No user ID found' };
    }

    setProfileLoading(true);
    
    try {
      if (Platform.OS === 'web') {
        window.dispatchEvent(new CustomEvent('toast', { detail: { type: 'info', text: 'Removing photo…' } }));
      }
    } catch {}

    try {
      // Step 1: Delete from Storage if URL exists
      const currentPhotoURL = user?.profilePhoto || user?.photoURL;
      if (currentPhotoURL) {
        try {
          // Extract the path from the URL or construct it
          // If it's a Storage URL, we can parse it, otherwise try to delete by known path
          // Try to delete all files in user's folder (best effort)
          // Note: Storage doesn't have list/delete all, so we'll just clear Firestore
          // The old file will remain but won't be accessible via rules
        } catch (storageError) {
          // Continue anyway
        }
      }

      // Step 2: Clear from Firestore
      const userRef = doc(db, 'users', user.uid);
      
      await setDoc(userRef, {
        profilePhoto: '',
        photoURL: '',
        updatedAt: new Date().toISOString()
      }, { merge: true });

      // Step 3: Update all local state
      setUser((prev) => prev ? { ...prev, profilePhoto: '', photoURL: '' } : prev);
      setUserProfile((prev) => prev ? { ...prev, profilePhoto: '', photoURL: '' } : prev);

      // Step 4: Reload profile
      await loadUserProfile();

      // Step 5: Success toast
      if (Platform.OS === 'web') {
        window.dispatchEvent(new CustomEvent('toast', {
          detail: { type: 'success', text: 'Profile photo removed' }
        }));
      }

      return { success: true };

    } catch (error) {
      console.error('[removeProfilePhoto] Error:', error);
      
      if (Platform.OS === 'web') {
        window.dispatchEvent(new CustomEvent('toast', {
          detail: { type: 'error', text: `Failed to remove photo: ${error.message}` }
        }));
      }
      
      return { success: false, error: error.message };
    } finally {
      setProfileLoading(false);
    }
  };

  // Update user preferences
  const updateUserPreferences = async (preferences) => {
    if (!user?.uid) {
      return { success: false, error: 'No user ID found' };
    }

    const updatedProfile = { preferences };
    const result = await saveUserProfile(updatedProfile);
    
    // Update local user state with new preferences
    if (result.success) {
      setUser(prev => prev ? { ...prev, preferences } : prev);
    }
    
    return result;
  };

  const deleteAccount = async () => {
    const { error } = await authService.deleteAccount();
    if (!error) {
      setUser(null);
      setUserProfile(null);
      lastUidRef.current = null;
      // Navigation will be handled by NavigationContext when it detects user is null
    }
    return { error };
  };

  const updateUserCommitment = async (commitmentLevel) => {
    if (!user?.uid) {
      return { success: false, error: 'No user ID found' };
    }

    const { success, error } = await authService.updateUserCommitment(user.uid, commitmentLevel);
    
    // Update local user state with commitment data
    if (success) {
      setUser(prev => prev ? { 
        ...prev, 
        commitmentLevel,
        hasCompletedIntake: true,
        intakeCompletedAt: new Date(),
      } : prev);
    }
    
    return { success, error };
  };

  return (
    <AuthContext.Provider value={{
      user,
      setUser,
      loading,
      devModeUser,
      setDevModeUser,
      logout,
      register,
      signIn,
      signInWithGoogleAuth,
      resetPassword,
      // User profile management
      userProfile,
      profileLoading,
      saveUserProfile,
      uploadProfilePhoto,
      removeProfilePhoto,
      updateUserPreferences,
      deleteAccount,
      loadUserProfile,
      updateUserCommitment,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within a AuthProvider');
  }
  return context;
}

